import { useState, useEffect, useCallback } from 'react'
import { AppState, ModuleId, SubSkillProgress } from '../types'
import { MODULES, QUESTION_BY_ID } from '../data/questionLoader'
import {
  GroupId,
  StartingPeriod,
  getStartingLevelForPeriod,
} from '../config/systemConfig'
import {
  updateQuestionState,
  getAnsweredQuestionsForGroup,
  getRecentQuestionsForGroup,
  getReviewNeededCountForGroup,
  getQuestionStateKey,
} from '../utils/questionAttemptHistory'
import {
  updateFamilyMastery,
  getFamilyMasteryKey,
  isGroupScopedKey,
} from '../utils/familyMasteryEngine'
import { getFamilyDefinition } from '../config/taxonomyRegistry'
import { getPlacementKey } from '../utils/placementEngine'
import {
  resolvePhaseByDate,
  getAutoStartingTier,
} from '../utils/dateBasedPlacement'
import { getModuleMasteryFromFamilies } from '../utils/masteryCanonical'
import { getGroupForTier } from '../config/tiers'
import {
  STORAGE_KEY,
  LEGACY_STORAGE_KEY,
  XP_PER_LEVEL,
  XP_REWARDS,
} from '../config/constants'
import { AccountsState } from '../types/accounts'
import {
  getActiveAccount,
  updateAccountName,
  updateAccountEmail as updateAccountEmailHelper,
  updateTeacherDetails as updateTeacherDetailsHelper,
  switchActiveAccount,
  ensureAdminAccount,
  generateAccountId,
  linkParentToLearner,
  linkTeacherToLearner,
} from '../utils/accountHelpers'
import {
  loadStateV3,
  createFreshV3State,
  saveV3State,
  MultiAccountStateV3,
} from '../utils/accountMigration'
import { generateDemoAccounts } from '../utils/demoAccountSeeder'
import { safeLocalStorageGet, debug, warn } from '../utils/logger'
import { safeStorageWrite } from '../utils/storageQuota'
const INITIAL_STATE: AppState = {
  stats: {
    name: 'Djayden',
    xp: 0,
    level: 1,
  },
  progress: {
    Metriek: {
      cheatSheetViewed: false,
    },
    Getalbegrip: {
      cheatSheetViewed: false,
    },
    TijdGeld: {
      cheatSheetViewed: false,
    },
    SpellingWW: {
      cheatSheetViewed: false,
    },
    SpellingRegel: {
      cheatSheetViewed: false,
    },
    Taalbegrip: {
      cheatSheetViewed: false,
    },
    Studievaardig: {
      cheatSheetViewed: false,
    },
    Topo: {
      cheatSheetViewed: false,
    },
  },
  questionStates: {},
  pendingLevelUp: undefined,
  placementResults: {},
  familyMastery: {},
}
export function useAppStore() {
  // Multi-account state (V3)
  const [multiAccountState, setMultiAccountState] =
    useState<MultiAccountStateV3 | null>(null)
  const [isInitialized, setIsInitialized] = useState(false)

  // NEW: Viewing context for parent/teacher (which learner they're viewing)
  const [viewingLearnerId, setViewingLearnerIdRaw] = useState<string | null>(
    null,
  )

  // Group selection (separate from user data)
  const [selectedGroup, setSelectedGroupRaw] = useState<GroupId>(6)
  const [manualPeriod, setManualPeriod] = useState<StartingPeriod | null>(null)
  const [groupVersion, setGroupVersion] = useState(0)

  // Derived: Active learner's state (now uses viewingLearnerId for parent/teacher)
  const state: AppState = (() => {
    if (!multiAccountState) {
      return INITIAL_STATE
    }
    const activeAccount = getActiveAccount(multiAccountState.accounts)
    if (!activeAccount) {
      warn('No active account found, using initial state')
      return INITIAL_STATE
    }

    // For learner accounts, return their data directly
    if (activeAccount.role === 'learner') {
      const learnerData = multiAccountState.learnerData[activeAccount.id]
      if (!learnerData) {
        warn('Active learner data not found, using initial state')
        return INITIAL_STATE
      }
      return learnerData
    }

    // For parent/teacher accounts, use viewingLearnerId if explicitly set
    if (activeAccount.role === 'parent' || activeAccount.role === 'teacher') {
      if (viewingLearnerId) {
        const learnerData = multiAccountState.learnerData[viewingLearnerId]
        if (learnerData) {
          return learnerData
        }
      }
    }
    return INITIAL_STATE
  })()
  const accounts: AccountsState = multiAccountState
    ? multiAccountState.accounts
    : createFreshV3State().accounts

  // Load state from localStorage on mount
  useEffect(() => {
    // ── One-time migration from legacy storage key ──────────────────────────
    // If the new key has no data but the legacy key does, migrate it over.
    const hasNewKey = !!localStorage.getItem(STORAGE_KEY)
    const legacyRaw = localStorage.getItem(LEGACY_STORAGE_KEY)
    if (!hasNewKey && legacyRaw) {
      debug('Migrating state from legacy STORAGE_KEY to new key')
      safeStorageWrite(STORAGE_KEY, legacyRaw)
      // Also migrate auxiliary keys (_group, _period)
      const legacyGroup = localStorage.getItem(`${LEGACY_STORAGE_KEY}_group`)
      const legacyPeriod = localStorage.getItem(`${LEGACY_STORAGE_KEY}_period`)
      if (legacyGroup) safeStorageWrite(`${STORAGE_KEY}_group`, legacyGroup)
      if (legacyPeriod) safeStorageWrite(`${STORAGE_KEY}_period`, legacyPeriod)
      // Clean up legacy keys
      localStorage.removeItem(LEGACY_STORAGE_KEY)
      localStorage.removeItem(`${LEGACY_STORAGE_KEY}_group`)
      localStorage.removeItem(`${LEGACY_STORAGE_KEY}_period`)
      debug('Legacy storage key migration complete')
    }

    // SAFE: Use safe localStorage getter with fallback
    const storedGroup = safeLocalStorageGet<GroupId>(`${STORAGE_KEY}_group`, 6)
    const storedPeriod = safeLocalStorageGet<StartingPeriod | null>(
      `${STORAGE_KEY}_period`,
      null,
    )
    setSelectedGroupRaw(storedGroup)
    if (storedPeriod) {
      setManualPeriod(storedPeriod)
    }

    // Load strict V3 state (unsupported schema reset)
    const loadedState = loadStateV3(STORAGE_KEY)

    // Post-load migration: ensure admin account exists
    const migratedState = {
      ...loadedState,
      accounts: ensureAdminAccount(loadedState.accounts),
    }

    setMultiAccountState(migratedState)
    saveV3State(STORAGE_KEY, migratedState)
    setIsInitialized(true)
  }, [])

  // Save state to localStorage on change
  useEffect(() => {
    if (isInitialized && multiAccountState) {
      saveV3State(STORAGE_KEY, multiAccountState)
    }
  }, [multiAccountState, isInitialized])

  // Save group and period selection
  useEffect(() => {
    if (isInitialized) {
      safeStorageWrite(`${STORAGE_KEY}_group`, JSON.stringify(selectedGroup))
    }
  }, [selectedGroup, isInitialized])
  useEffect(() => {
    if (isInitialized) {
      if (manualPeriod) {
        safeStorageWrite(`${STORAGE_KEY}_period`, JSON.stringify(manualPeriod))
      } else {
        localStorage.removeItem(`${STORAGE_KEY}_period`)
      }
    }
  }, [manualPeriod, isInitialized])

  // Group switching
  const setSelectedGroup = useCallback((group: GroupId) => {
    setSelectedGroupRaw(group)
    setGroupVersion((v) => v + 1)
    debug(`Group switched to ${group}`)
  }, [])

  // Manual period switching (override)
  const setStartingPeriod = useCallback((period: StartingPeriod | null) => {
    setManualPeriod(period)
    setGroupVersion((v) => v + 1)
    debug(`Starting period set to ${period || 'AUTO'} - context updated`)
  }, [])

  // NEW: Set viewing learner (for parent/teacher portals)
  const setViewingLearner = useCallback((learnerId: string | null) => {
    setViewingLearnerIdRaw(learnerId)
    debug(`Viewing learner set to: ${learnerId || 'none'}`)
  }, [])

  // Computed effective period (for UI display)
  const startingPeriod = manualPeriod || resolvePhaseByDate()

  // Update active learner's state
  const updateLearnerState = useCallback(
    (updater: (prev: AppState) => AppState) => {
      setMultiAccountState((prev) => {
        if (!prev) return prev
        const activeAccount = getActiveAccount(prev.accounts)
        if (!activeAccount) return prev

        // Only allow updates for learner accounts
        if (activeAccount.role !== 'learner') {
          warn('Cannot update state for non-learner account')
          return prev
        }
        const currentState = prev.learnerData[activeAccount.id]
        const newState = updater(currentState)
        return {
          ...prev,
          learnerData: {
            ...prev.learnerData,
            [activeAccount.id]: newState,
          },
        }
      })
    },
    [],
  )

  // Account management functions
  const updateProfileDisplayName = useCallback((newName: string) => {
    setMultiAccountState((prev) => {
      if (!prev) return prev
      const activeAccount = getActiveAccount(prev.accounts)
      if (!activeAccount) return prev
      const updatedAccounts = updateAccountName(
        prev.accounts,
        activeAccount.id,
        newName,
      )
      return {
        ...prev,
        accounts: updatedAccounts,
      }
    })
  }, [])

  const updateAccountEmail = useCallback((accountId: string, email: string) => {
    setMultiAccountState((prev) => {
      if (!prev) return prev
      const updatedAccounts = updateAccountEmailHelper(
        prev.accounts,
        accountId,
        email,
      )
      return {
        ...prev,
        accounts: updatedAccounts,
      }
    })
  }, [])

  /**
   * Update any account's display name by ID.
   * Unlike updateProfileDisplayName (which updates the active account),
   * this targets a specific account — used by admin UserEditPanel.
   */
  const updateAccountNameById = useCallback(
    (accountId: string, name: string) => {
      setMultiAccountState((prev) => {
        if (!prev) return prev
        const updatedAccounts = updateAccountName(
          prev.accounts,
          accountId,
          name,
        )
        return {
          ...prev,
          accounts: updatedAccounts,
        }
      })
    },
    [],
  )

  const updateTeacherDetails = useCallback(
    (
      teacherId: string,
      updates: { schoolName?: string; classroomName?: string },
    ) => {
      setMultiAccountState((prev) => {
        if (!prev) return prev
        const updatedAccounts = updateTeacherDetailsHelper(
          prev.accounts,
          teacherId,
          updates,
        )
        return {
          ...prev,
          accounts: updatedAccounts,
        }
      })
    },
    [],
  )

  /**
   * Create a new account
   */
  const createAccount = useCallback(
    (
      role: 'learner' | 'teacher' | 'parent' | 'admin',
      details: {
        displayName: string
        email?: string
        schoolName?: string
        classroomName?: string
        grade?: number
      },
    ) => {
      const newId = generateAccountId()
      const now = Date.now()

      setMultiAccountState((prev) => {
        if (!prev) return prev

        const newAccount: any = {
          id: newId,
          role,
          displayName: details.displayName,
          email: details.email,
          createdAt: now,
          lastActive: now,
        }

        if (role === 'teacher') {
          newAccount.schoolName = details.schoolName
          newAccount.classroomName = details.classroomName
        } else if (role === 'learner') {
          newAccount.grade = details.grade
        }

        // If creating a learner, also initialize their AppState
        const newLearnerData =
          role === 'learner'
            ? {
                [newId]: {
                  ...INITIAL_STATE,
                  stats: { ...INITIAL_STATE.stats, name: details.displayName },
                },
              }
            : {}

        return {
          ...prev,
          accounts: {
            ...prev.accounts,
            accountsById: {
              ...prev.accounts.accountsById,
              [newId]: newAccount,
            },
          },
          learnerData: {
            ...prev.learnerData,
            ...newLearnerData,
          },
        }
      })

      return newId
    },
    [],
  )

  /**
   * Delete an account
   */
  const deleteAccount = useCallback((accountId: string) => {
    setMultiAccountState((prev) => {
      if (!prev) return prev

      // Don't allow deleting the last admin
      const account = prev.accounts.accountsById[accountId]
      if (account?.role === 'admin') {
        const adminCount = Object.values(prev.accounts.accountsById).filter(
          (a) => a.role === 'admin',
        ).length
        if (adminCount <= 1) return prev
      }

      const newAccountsById = { ...prev.accounts.accountsById }
      delete newAccountsById[accountId]

      // Clean up relationships
      const newRelationships = {
        parentToLearners: { ...prev.accounts.relationships.parentToLearners },
        learnerToParents: { ...prev.accounts.relationships.learnerToParents },
        teacherToLearners: { ...prev.accounts.relationships.teacherToLearners },
      }

      if (account?.role === 'parent') {
        delete newRelationships.parentToLearners[accountId]
        Object.keys(newRelationships.learnerToParents).forEach((lId) => {
          newRelationships.learnerToParents[lId] =
            newRelationships.learnerToParents[lId].filter(
              (id) => id !== accountId,
            )
        })
      } else if (account?.role === 'teacher') {
        delete newRelationships.teacherToLearners[accountId]
      } else if (account?.role === 'learner') {
        delete newRelationships.learnerToParents[accountId]
        Object.keys(newRelationships.parentToLearners).forEach((pId) => {
          newRelationships.parentToLearners[pId] =
            newRelationships.parentToLearners[pId].filter(
              (id) => id !== accountId,
            )
        })
        Object.keys(newRelationships.teacherToLearners).forEach((tId) => {
          newRelationships.teacherToLearners[tId] =
            newRelationships.teacherToLearners[tId].filter(
              (id) => id !== accountId,
            )
        })
      }

      // Clean up learner data if it was a learner
      const newLearnerData = { ...prev.learnerData }
      if (account?.role === 'learner') {
        delete newLearnerData[accountId]
      }

      return {
        ...prev,
        accounts: {
          ...prev.accounts,
          accountsById: newAccountsById,
          relationships: newRelationships,
          // If active account was deleted, switch to the first available one
          activeAccountId:
            prev.accounts.activeAccountId === accountId
              ? Object.keys(newAccountsById)[0] || ''
              : prev.accounts.activeAccountId,
        },
        learnerData: newLearnerData,
      }
    })
  }, [])

  /**
   * Link parent to learner
   */
  const linkParentToLearnerById = useCallback(
    (parentId: string, learnerId: string) => {
      setMultiAccountState((prev) => {
        if (!prev) return prev
        return {
          ...prev,
          accounts: linkParentToLearner(prev.accounts, parentId, learnerId),
        }
      })
    },
    [],
  )

  /**
   * Link teacher to learner
   */
  const linkTeacherToLearnerById = useCallback(
    (teacherId: string, learnerId: string) => {
      setMultiAccountState((prev) => {
        if (!prev) return prev
        return {
          ...prev,
          accounts: linkTeacherToLearner(prev.accounts, teacherId, learnerId),
        }
      })
    },
    [],
  )

  /**
   * Unlink parent from learner
   */
  const unlinkParentFromLearner = useCallback(
    (parentId: string, learnerId: string) => {
      setMultiAccountState((prev) => {
        if (!prev) return prev
        const rels = prev.accounts.relationships
        return {
          ...prev,
          accounts: {
            ...prev.accounts,
            relationships: {
              ...rels,
              parentToLearners: {
                ...rels.parentToLearners,
                [parentId]: (rels.parentToLearners[parentId] || []).filter(
                  (id) => id !== learnerId,
                ),
              },
              learnerToParents: {
                ...rels.learnerToParents,
                [learnerId]: (rels.learnerToParents[learnerId] || []).filter(
                  (id) => id !== parentId,
                ),
              },
            },
          },
        }
      })
    },
    [],
  )

  /**
   * Unlink teacher from learner
   */
  const unlinkTeacherFromLearner = useCallback(
    (teacherId: string, learnerId: string) => {
      setMultiAccountState((prev) => {
        if (!prev) return prev
        const rels = prev.accounts.relationships
        return {
          ...prev,
          accounts: {
            ...prev.accounts,
            relationships: {
              ...rels,
              teacherToLearners: {
                ...rels.teacherToLearners,
                [teacherId]: (rels.teacherToLearners[teacherId] || []).filter(
                  (id) => id !== learnerId,
                ),
              },
            },
          },
        }
      })
    },
    [],
  )

  /**
   * Switch to a different account
   */
  const switchAccount = useCallback((accountId: string) => {
    setMultiAccountState((prev) => {
      if (!prev) return prev
      const updatedAccounts = switchActiveAccount(prev.accounts, accountId)
      return {
        ...prev,
        accounts: updatedAccounts,
      }
    })

    // Clear viewing context when switching accounts
    setViewingLearnerIdRaw(null)
    debug(`🔄 Switched to account: ${accountId}`)
  }, [])

  /**
   * Get a specific learner's data by ID.
   * Used by parent/teacher portals to view individual learner progress.
   */
  const getLearnerData = useCallback(
    (learnerId: string): AppState | null => {
      if (!multiAccountState) return null
      return multiAccountState.learnerData[learnerId] || null
    },
    [multiAccountState],
  )

  /**
   * Get module mastery for a specific learner and module.
   * Used by parent/teacher portals to view learner-specific progress.
   */
  const getLearnerModuleMastery = useCallback(
    (learnerId: string, moduleId: ModuleId): number => {
      const learnerData = getLearnerData(learnerId)
      if (!learnerData) return 0
      return getModuleMasteryFromFamilies(
        learnerData.familyMastery || {},
        moduleId,
        selectedGroup,
      )
    },
    [getLearnerData, selectedGroup],
  )
  const markCheatSheetViewed = (moduleId: ModuleId) => {
    updateLearnerState((prev) => ({
      ...prev,
      progress: {
        ...prev.progress,
        [moduleId]: {
          ...prev.progress[moduleId],
          cheatSheetViewed: true,
        },
      },
    }))
  }
  const recordAnswer = (
    moduleId: ModuleId,
    questionId: string,
    isCorrect: boolean,
    userChoice: number,
    payload?: string,
    errorType?: string,
    delta?: number,
  ) => {
    updateLearnerState((prev) => {
      const question = QUESTION_BY_ID.get(questionId)
      const questionGroup = question?.group || selectedGroup

      // Use group-scoped key for question state
      const stateKey = getQuestionStateKey(questionId, questionGroup)
      const currentQuestionState = prev.questionStates[stateKey]
      const newQuestionState = updateQuestionState(
        currentQuestionState,
        questionId,
        isCorrect,
      )
      const newQuestionStates = {
        ...prev.questionStates,
        [stateKey]: newQuestionState,
      }

      // Update familyId mastery tracking (CANONICAL MASTERY SOURCE - GROUP-SCOPED)
      let newFamilyMastery = prev.familyMastery ?? {}
      if (question?.familyId) {
        const familyId = question.familyId
        const questionTier =
          question.tier ?? getFamilyDefinition(familyId)?.tier ?? 1

        // CRITICAL: Use selectedGroup for mastery key (not question.group)
        // This ensures progress is always recorded in the active group
        const masteryKey = getFamilyMasteryKey(familyId, selectedGroup)
        const currentFamilyState = newFamilyMastery[masteryKey]
        const updatedFamilyState = updateFamilyMastery(
          currentFamilyState,
          familyId,
          // Keep original familyId in state
          isCorrect,
          questionId,
          questionTier,
          payload,
          errorType,
          delta,
        )
        newFamilyMastery = {
          ...newFamilyMastery,
          [masteryKey]: updatedFamilyState,
        }
      }

      // XP calculation using tier directly
      let xpGain = XP_REWARDS.EFFORT
      if (isCorrect && question) {
        const tier = question.tier ?? 1
        if (tier >= 13) {
          xpGain = XP_REWARDS.CORRECT_HARD
        } else if (tier >= 7) {
          xpGain = XP_REWARDS.CORRECT_MEDIUM
        } else {
          xpGain = XP_REWARDS.CORRECT_EASY
        }
      } else if (isCorrect) {
        xpGain = XP_REWARDS.CORRECT_EASY
      }
      const newXP = prev.stats.xp + xpGain
      const oldLevel = prev.stats.level
      const newLevel = Math.floor(newXP / XP_PER_LEVEL) + 1
      const pendingLevelUp =
        newLevel > oldLevel ? newLevel : prev.pendingLevelUp
      return {
        ...prev,
        stats: {
          ...prev.stats,
          xp: newXP,
          level: newLevel,
        },
        progress: prev.progress,
        questionStates: newQuestionStates,
        familyMastery: newFamilyMastery,
        pendingLevelUp,
      }
    })
  }
  const clearPendingLevelUp = () => {
    updateLearnerState((prev) => ({
      ...prev,
      pendingLevelUp: undefined,
    }))
  }
  const devJumpToLevel = (targetLevel: number) => {
    updateLearnerState((prev) => {
      const xpNeeded = (targetLevel - 1) * XP_PER_LEVEL
      return {
        ...prev,
        stats: {
          ...prev.stats,
          xp: xpNeeded,
          level: targetLevel,
        },
      }
    })
  }
  const devResetProgress = () => {
    const freshState = createFreshV3State(true)
    setMultiAccountState(freshState)
    saveV3State(STORAGE_KEY, freshState)
    setGroupVersion((v) => v + 1)
  }
  const savePlacementResult = useCallback(
    (result: import('../types').PlacementResult) => {
      updateLearnerState((prev) => {
        const key = getPlacementKey(result.moduleId, result.group as GroupId)
        return {
          ...prev,
          placementResults: {
            ...prev.placementResults,
            [key]: result,
          },
        }
      })
    },
    [updateLearnerState],
  )

  /**
   * Get the starting tier for a module+group.
   * Priority:
   * 1) Placement test result (if exists)
   * 2) Manual override (if set)
   * 3) Auto-detected date-based tier
   */
  const getStartingLevel = useCallback(
    (
      moduleId: import('../types').ModuleId,
      group: GroupId,
    ): number | undefined => {
      // 1. Check for per-module placement test result
      const key = getPlacementKey(moduleId, group)
      const placementResult = state.placementResults?.[key]
      if (placementResult) {
        return placementResult.startingTier
      }

      // 2. Check for manual override
      if (manualPeriod) {
        return getStartingLevelForPeriod(group, manualPeriod)
      }

      // 3. Use auto-detected date-based tier
      return getAutoStartingTier(group)
    },
    [state.placementResults, manualPeriod],
  )
  const hasPlacement = useCallback(
    (moduleId: import('../types').ModuleId, group: GroupId): boolean => {
      const key = getPlacementKey(moduleId, group)
      return !!state.placementResults?.[key]
    },
    [state.placementResults],
  )

  /**
   * Get group-scoped module mastery percentage.
   * Computes mastery on-demand from family mastery (CANONICAL).
   */
  const getModuleMasteryForGroup = useCallback(
    (moduleId: ModuleId): number => {
      return getModuleMasteryFromFamilies(
        state.familyMastery || {},
        moduleId,
        selectedGroup,
      )
    },
    [state.familyMastery, selectedGroup],
  )

  /**
   * Get list of answered questions for a module in the current group.
   */
  const getAnsweredQuestions = useCallback(
    (moduleId: ModuleId): string[] => {
      return getAnsweredQuestionsForGroup(
        moduleId,
        state.questionStates,
        selectedGroup,
      )
    },
    [state.questionStates, selectedGroup],
  )

  /**
   * Get recent questions for a module in the current group.
   */
  const getRecentQuestions = useCallback(
    (moduleId: ModuleId, limit?: number): string[] => {
      return getRecentQuestionsForGroup(
        moduleId,
        state.questionStates,
        selectedGroup,
        limit,
      )
    },
    [state.questionStates, selectedGroup],
  )

  /**
   * Get count of questions needing review for a module in the current group.
   */
  const getReviewNeededCount = useCallback(
    (moduleId: ModuleId): number => {
      return getReviewNeededCountForGroup(
        moduleId,
        state.questionStates,
        selectedGroup,
      )
    },
    [state.questionStates, selectedGroup],
  )

  // Get active account
  const activeAccount = multiAccountState
    ? getActiveAccount(multiAccountState.accounts)
    : null
  const currentLearnerDisplayName = (() => {
    if (!activeAccount || !multiAccountState) {
      return INITIAL_STATE.stats.name
    }
    if (activeAccount.role === 'learner') {
      return activeAccount.displayName
    }
    if (viewingLearnerId) {
      const viewedLearner =
        multiAccountState.accounts.accountsById[viewingLearnerId]
      if (viewedLearner?.role === 'learner') {
        return viewedLearner.displayName
      }
    }
    return INITIAL_STATE.stats.name
  })()
  const requiresViewingLearnerSelection = Boolean(
    activeAccount &&
      activeAccount.role !== 'learner' &&
      activeAccount.role !== 'admin' &&
      !viewingLearnerId,
  )

  /**
   * Reset a learner's progress data entirely.
   * Used by admin portal with double confirmation in UI.
   * Resets: questionStates, familyMastery, stats (xp=0, level=1), placementResults.
   */
  const resetLearnerProgress = useCallback((learnerId: string) => {
    setMultiAccountState((prev) => {
      if (!prev) return prev
      const learnerData = prev.learnerData[learnerId]
      if (!learnerData) return prev

      const resetData: AppState = {
        stats: {
          name: learnerData.stats.name,
          xp: 0,
          level: 1,
        },
        progress: {
          Metriek: { cheatSheetViewed: false },
          Getalbegrip: { cheatSheetViewed: false },
          TijdGeld: { cheatSheetViewed: false },
          SpellingWW: { cheatSheetViewed: false },
          SpellingRegel: { cheatSheetViewed: false },
          Taalbegrip: { cheatSheetViewed: false },
          Studievaardig: { cheatSheetViewed: false },
          Topo: { cheatSheetViewed: false },
        },
        questionStates: {},
        pendingLevelUp: undefined,
        placementResults: {},
        familyMastery: {},
      }

      return {
        ...prev,
        learnerData: {
          ...prev.learnerData,
          [learnerId]: resetData,
        },
      }
    })
    debug(`🗑️ Reset progress for learner: ${learnerId}`)
  }, [])

  /**
   * Load demo accounts with realistic progress data
   */
  const loadDemoAccounts = useCallback(() => {
    const demoState = generateDemoAccounts()
    setMultiAccountState(demoState)
    saveV3State(STORAGE_KEY, demoState)
    setGroupVersion((v) => v + 1)
    debug('✅ Demo accounts loaded successfully')
  }, [])

  return {
    state,
    accounts,
    activeAccount,
    currentLearnerDisplayName,
    requiresViewingLearnerSelection,
    isInitialized,
    selectedGroup,
    setSelectedGroup,
    startingPeriod,
    manualPeriod,
    setStartingPeriod,
    groupVersion,
    markCheatSheetViewed,
    recordAnswer,
    clearPendingLevelUp,
    devJumpToLevel,
    devResetProgress,
    savePlacementResult,
    getStartingLevel,
    hasPlacement,
    getModuleMasteryForGroup,
    getAnsweredQuestions,
    getRecentQuestions,
    getReviewNeededCount,
    updateProfileDisplayName,
    updateAccountNameById,
    getLearnerData,
    getLearnerModuleMastery,
    switchAccount,
    updateAccountEmail,
    updateTeacherDetails,
    createAccount,
    deleteAccount,
    linkParentToLearnerById,
    linkTeacherToLearnerById,
    unlinkParentFromLearner,
    unlinkTeacherFromLearner,
    resetLearnerProgress,
    // NEW: Viewing context for parent/teacher portals
    viewingLearnerId,
    setViewingLearner,
    loadDemoAccounts,
    // Account CRUD
    // Account CRUD
  }
}
